<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';
    import { RESULT_MAPPING_KEY } from '$lib/global/constants';

    import { page } from '$app/stores';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { trackAnswerClick } from '$lib/global/tracking';
    import { getColor, getColorByLightness, personalizeText } from '$lib/helper';
    import { quizMetrics, disabledNavigation, advancedLinking } from '$lib/Stores/stores';
    import { getLinkingPreloadTarget, handleLinkingNavigation } from '$lib/utils/linkingNavigation';
    import { storeUserAnswer, getAllUserAnswers } from '$lib/utils/userAnswerStorage';

    import get from 'lodash/get';
    import { onDestroy, onMount } from 'svelte';

    import TextQuestionView from './TextQuestion.View.svelte';

    import type { Component } from '$lib/types/Components/SharedTypes';
    import type { TextQuestionContent } from '$lib/types/Components/TextQuestion/TextQuestionContent';
    import type { Field } from '$lib/types/Field';
    import type { Language } from '$lib/types/Language';
    import type { ResultMapping } from '$lib/types/ResultMapping';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { TrackingProperty } from '$lib/types/TrackingProperty';

    export let id = '';
    export let content: TextQuestionContent;
    export let childComponents: Component[] = [];
    export let fields: Field[] = [];
    export let theme: Theme;
    export let resultMapping: ResultMapping;
    export let currentPage = '';
    export let nextPage = '';
    export let campaignId = '';
    export let campaignVersionId = '';
    export let firstResultPage = '';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'none';
    export let trackingProperties: TrackingProperty[] = [];
    export let language: Language = 'en';
    export let layout = DEFAULT_LAYOUT;

    let clickedAnswer = '';
    let isResultVisible = false;
    let navigationTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

    const {
        data: { pageIdSlugMap },
    } = $page;

    // Navigation
    const nextPageNavigator = new NextPageNavigator(
        campaignId,
        firstResultPage,
        pageIdSlugMap,
        trackingProperties,
    );

    let personalizedText = content.wysiwyg;

    onMount(async () => {
        personalizedText = personalizeText(
            personalizedText,
            trackingProperties,
            campaignId,
            language,
        );
    });

    onDestroy(() => {
        if (navigationTimeout) {
            clearTimeout(navigationTimeout);
        }
    });

    $: quizResult = $quizMetrics.find((metric) => metric.insightId === content.fieldName);

    $: totalQuizResults = Object.values(quizResult?.aggregates || {}).reduce(
        (acc, curr) => acc + curr,
        0,
    );

    $: answers = childComponents.map((answer) => {
        const bgColor = getColor(
            get(answer, 'content.backgroundColor'),
            theme.formFieldBackgroundColor,
        );

        const plainText = get(answer, 'content.plainText', '');
        const count = quizResult?.aggregates[plainText] ?? 0;

        const responsePercentage =
            typeof totalQuizResults === 'number' && clickedAnswer
                ? (count + (clickedAnswer === answer._id ? 1 : 0)) / (totalQuizResults + 1)
                : undefined;

        return {
            id: answer._id,
            color: getColor(get(answer, 'content.color'), getColorByLightness(bgColor)),
            bgColor,
            emoji: get(answer, 'content.emoji', ':smiley:'),
            icon: get(answer, 'content.icon', ''),
            showIcon: get(answer, 'content.showIcon', false),
            srcType: get(answer, 'content.srcType', ''),
            iconName: get(answer, 'content.iconName', ''),
            platform: get(answer, 'content.platform', ''),
            iconColor: getColor(get(answer, 'content.iconColor'), theme.buttonBackgroundColor),
            svg: get(answer, 'content.svg', ''),
            isAnimated: get(answer, 'content.isAnimated', false),
            jsonUrl: get(answer, 'content.jsonUrl', null),
            draft: personalizeText(
                answer.content.wysiwyg,
                trackingProperties,
                campaignId,
                language,
            ),
            resultId: get(resultMapping, `${currentPage}.${answer._id}.destination`),
            dynamicNextPage: get(answer, 'content.linking.pageId'),
            linkingComponentId: get(answer, 'content.linking.componentId'),
            link: get(answer, 'content.linking.isInternal', true)
                ? ''
                : get(answer, 'content.linking.url', ''),
            isNewTab: get(answer, 'content.linking.isNewTab', false),
            responsePercentage,
            isResultVisible,
        };
    });

    const handleAnswerClick = (resultId: string, answerId: string, link: string) => {
        const answer = answers.find((answer) => answer.id === answerId);
        const dynamicNextPage = answer?.dynamicNextPage;
        const linkingComponentId = answer?.linkingComponentId;
        const isNewTab = answer?.isNewTab ?? false;
        const revealDuration = content.revealDuration;

        clickedAnswer = answerId;

        // Check for undefined or 0 because 0 is a valid revealDuration
        if (revealDuration) {
            isResultVisible = true;

            // Disable navigation in funnel
            disabledNavigation.set(true);
        }

        // Store user answer
        storeUserAnswer({
            campaignVersionId,
            questionId: id,
            answerId,
        });

        // Tracking
        trackAnswerClick({
            answer: childComponents.find((child) => child._id === answerId),
            question: { id, content },
            fields,
        });

        if (resultId) {
            // Increment count for result page
            const ST = window.sessionStorage;
            const resultCounts = JSON.parse(
                ST.getItem(`${RESULT_MAPPING_KEY}${campaignId}`) || '{}',
            );
            const resultCount = get(resultCounts, resultId, 0) + 1;
            const updatedResultCounts = {
                ...resultCounts,
                [resultId]: resultCount,
            };

            // Save updated result count to session storage
            ST.setItem(`${RESULT_MAPPING_KEY}${campaignId}`, JSON.stringify(updatedResultCounts));
        }

        navigationTimeout = setTimeout(
            () => {
                // Enable navigation in funnel again
                disabledNavigation.set(false);

                return handleLinkingNavigation({
                    componentId: answerId,
                    userAnswers: getAllUserAnswers(campaignVersionId),
                    advancedLinking: $advancedLinking,
                    nextPageNavigator,
                    originalLogicNextPage: dynamicNextPage || nextPage,
                    nextPage,
                    externalLink: link,
                    isExternal: !!link,
                    isNewTab,
                    currentPageId: $page.data.pageId,
                    linkingComponentId,
                });
            },
            revealDuration ? revealDuration * 1000 : 0,
        );
    };

    const handleAnswerMouseEnter = (_resultId: string, answerId: string, link: string) => {
        const answer = answers.find((answer) => answer.id === answerId);
        const dynamicNextPage = answer?.dynamicNextPage;

        const { pageId, isExternal } = getLinkingPreloadTarget({
            componentId: answerId,
            userAnswers: getAllUserAnswers(campaignVersionId),
            advancedLinking: $advancedLinking,
            nextPageNavigator,
            originalLogicNextPage: dynamicNextPage || nextPage,
            nextPage,
            isExternal: !!link,
        });

        nextPageNavigator.preload(pageId, isExternal);
    };
</script>

<TextQuestionView
    {id}
    draft={personalizedText}
    align={content.align}
    color={getColor(content.color, theme.fontColor)}
    box={content.box}
    onAnswerClick={handleAnswerClick}
    onAnswerMouseEnter={handleAnswerMouseEnter}
    {answers}
    {transitionDelay}
    {transitionEffect}
    {layout}
/>
